import { fetcher } from '@commenda/utils';
import {
  GenerateInvoiceTaxBreakdownResponse,
  GenerateTaxBreakdownForFilingsParams,
  GenerateTaxBreakdownResponse,
  SyncFilingNumbersQueryParams,
  TriggerSyncFilingsResponse,
} from './types';

export async function generateTaxBreakdownForFilings(
  corporationId: number,
  params?: GenerateTaxBreakdownForFilingsParams
): Promise<{
  success: boolean;
  error?: string;
  data?: GenerateTaxBreakdownResponse['data'];
}> {
  try {
    // Validate that at least one of filing_id or both due dates is provided
    const hasFilingId = params?.filing_id !== undefined;
    const hasDateRange = params?.due_date_start && params?.due_date_end;
    const hasPartialDateRange =
      (params?.due_date_start || params?.due_date_end) && !hasDateRange;

    if (!hasFilingId && !hasDateRange) {
      return {
        success: false,
        error:
          'At least one of filing_id or both due_date_start and due_date_end must be provided',
      };
    }

    if (hasPartialDateRange) {
      return {
        success: false,
        error: 'Both due_date_start and due_date_end must be provided together',
      };
    }

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (hasFilingId)
      queryParams.append('filing_id', params!.filing_id!.toString());
    if (hasDateRange) {
      queryParams.append('due_date_start', params!.due_date_start!);
      queryParams.append('due_date_end', params!.due_date_end!);
    }

    const url = queryParams.toString()
      ? `new-sales-tax/corporations/${corporationId}/tax-breakdowns/generate?${queryParams.toString()}`
      : `new-sales-tax/corporations/${corporationId}/tax-breakdowns/generate`;

    const response = await fetcher(url, { method: 'POST' }, undefined);

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to trigger sync filings',
      };
    }

    return {
      success: true,
      data: data.data,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to trigger sync filings';
    return {
      success: false,
      error: errorMessage,
    };
  }
}

export async function generateTaxBreakdownForInvoice(
  corporationId: number,
  invoiceId: string
): Promise<{
  success: boolean;
  error?: string;
  data?: GenerateInvoiceTaxBreakdownResponse['data'];
}> {
  try {
    const url = `new-sales-tax/corporations/${corporationId}/invoice/${invoiceId}/tax-breakdowns/generate`;

    const response = await fetcher(url, { method: 'POST' }, undefined);

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to trigger sync filings',
      };
    }

    return {
      success: true,
      data: data.data,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to trigger sync filings';
    return {
      success: false,
      error: errorMessage,
    };
  }
}

export async function syncTaxBreakdownAggregatesForFilings(
  corporationId: number,
  params?: SyncFilingNumbersQueryParams
): Promise<{
  success: boolean;
  error?: string;
  data?: TriggerSyncFilingsResponse['data'];
}> {
  try {
    // Validate that at least one of filing_id or both due dates is provided
    const hasDateRange = params?.due_date_start && params?.due_date_end;
    const hasPartialDateRange =
      (params?.due_date_start || params?.due_date_end) && !hasDateRange;

    if (!hasDateRange) {
      return {
        success: false,
        error: 'due_date_start and due_date_end must be provided',
      };
    }

    if (hasPartialDateRange) {
      return {
        success: false,
        error: 'Both due_date_start and due_date_end must be provided together',
      };
    }

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (hasDateRange) {
      queryParams.append('due_date_start', params!.due_date_start!);
      queryParams.append('due_date_end', params!.due_date_end!);
    }

    const url = queryParams.toString()
      ? `new-sales-tax/corporations/${corporationId}/filing-aggregates/sync?${queryParams.toString()}`
      : `new-sales-tax/corporations/${corporationId}/filing-aggregates/sync`;

    const response = await fetcher(url, { method: 'POST' }, undefined);

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to sync filing aggregates',
      };
    }

    return {
      success: true,
      data: data.data,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to sync filing aggregates';
    return {
      success: false,
      error: errorMessage,
    };
  }
}
